import { styled } from '@linaria/react';
import { ScrollArea, Stack } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useOutcomes } from '../../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { Table } from '../../../../components/table/table';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import { setSelectedInitiative } from '../../../../store/strategy-store/strategy-store.actions';
import { TableGroupBy } from '../../../../store/strategy-store/strategy-store.type';
import { inkBase, secondaryBase, skyLight, skyLighter } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { useFilteredInitiatives, useFiltersApplied } from '../../strategy.hooks';
import type { InitiativeRecord } from '../../strategy.type';
import { getStatusLabels, SOURCE_LABELS } from '../filters/filters.type';
import { createSortTableWithOutcomes, getTableColumns } from './main-section.helpers';

type GroupedInitiatives = Record<string, InitiativeRecord[]>;

interface MainSectionTableGroupedProps {
  groupBy: TableGroupBy;
}

export const MainSectionTableGrouped = ({ groupBy }: MainSectionTableGroupedProps) => {
  const i18n = useTranslation();
  const statusLabels = useMemo(() => getStatusLabels(i18n.t), [i18n.t]);
  const selectedInitiative = useStrategyStore((state) => state.selectedInitiative);
  const { initiatives } = useFilteredInitiatives();
  const filtersApplied = useFiltersApplied();
  const { outcomes } = useOutcomes();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Create sort function with outcomes context
  const sortTableWithOutcomes = useMemo(() => createSortTableWithOutcomes(outcomes), [outcomes]);

  // Group initiatives based on groupBy prop
  const { groupedInitiatives, unassignedInitiatives, orderedGroupKeys } = useMemo(() => {
    const groups: GroupedInitiatives = {};
    let unmappedKey: string | null = null;

    if (groupBy === TableGroupBy.Outcome) {
      // Group by outcome - many-to-many (initiatives appear in all matching groups)
      for (const outcome of outcomes) {
        const outcomeInitiatives = initiatives.filter((initiative) =>
          outcome.initiatives?.some((init) => init.id === initiative.id),
        );

        if (outcomeInitiatives.length > 0) {
          groups[outcome.id] = outcomeInitiatives;
        }
      }

      // Get initiatives with no outcomes
      const unassigned = initiatives.filter(
        (init) => !outcomes.some((outcome) => outcome.initiatives?.some((oi) => oi.id === init.id)),
      );

      return {
        groupedInitiatives: groups,
        unassignedInitiatives: unassigned,
        orderedGroupKeys: Object.keys(groups),
      };
    } else if (groupBy === TableGroupBy.Team) {
      // Group by team - many-to-many (initiatives appear in all teams they contribute to)
      const teamNameToInitiatives = new Map<string, InitiativeRecord[]>();
      const unmappedInitiatives: InitiativeRecord[] = [];

      for (const initiative of initiatives) {
        const teamLabel = initiative.contributing_teams_label;

        if (teamLabel) {
          // Parse comma-separated team names
          const teamNames = teamLabel
            .split(',')
            .map((name) => name.trim())
            .filter((name) => name.length > 0);

          // Add initiative to each team group
          for (const teamName of teamNames) {
            if (!teamNameToInitiatives.has(teamName)) {
              teamNameToInitiatives.set(teamName, []);
            }
            teamNameToInitiatives.get(teamName)!.push(initiative);
          }
        } else {
          // Track initiatives with no teams
          unmappedInitiatives.push(initiative);
        }
      }

      // Add teams in sorted order
      const sortedTeamNames = Array.from(teamNameToInitiatives.keys()).sort((a, b) => a.localeCompare(b));

      for (const teamName of sortedTeamNames) {
        groups[teamName] = teamNameToInitiatives.get(teamName)!;
      }

      // Add unmapped group at the end
      if (unmappedInitiatives.length > 0) {
        groups['no-team'] = unmappedInitiatives;
        unmappedKey = 'no-team';
      }

      const orderedKeys = Object.keys(groups);
      if (unmappedKey && orderedKeys.includes(unmappedKey)) {
        const keysWithoutUnmapped = orderedKeys.filter((k) => k !== unmappedKey);
        return {
          groupedInitiatives: groups,
          unassignedInitiatives: [],
          orderedGroupKeys: [...keysWithoutUnmapped, unmappedKey],
        };
      }

      return { groupedInitiatives: groups, unassignedInitiatives: [], orderedGroupKeys: orderedKeys };
    } else if (groupBy === TableGroupBy.Status) {
      // Group by status - separate mapped and unmapped
      const mappedStatuses: string[] = [];
      const statusMap = new Map<string, InitiativeRecord[]>();

      for (const initiative of initiatives) {
        const status = initiative.status?.by_tasks?.status;
        if (status) {
          if (!statusMap.has(status)) {
            statusMap.set(status, []);
            mappedStatuses.push(status);
          }
          statusMap.get(status)!.push(initiative);
        }
      }

      // Add mapped statuses in sorted order
      for (const status of mappedStatuses.sort()) {
        groups[status] = statusMap.get(status)!;
      }

      // Get unmapped initiatives (those without a status)
      const unassigned = initiatives.filter((init) => !init.status?.by_tasks?.status);
      if (unassigned.length > 0) {
        groups['unknown'] = unassigned;
        unmappedKey = 'unknown';
      }

      const orderedKeys = Object.keys(groups);
      if (unmappedKey && orderedKeys.includes(unmappedKey)) {
        // Move unmapped to the end
        const keysWithoutUnmapped = orderedKeys.filter((k) => k !== unmappedKey);
        return {
          groupedInitiatives: groups,
          unassignedInitiatives: [],
          orderedGroupKeys: [...keysWithoutUnmapped, unmappedKey],
        };
      }

      return { groupedInitiatives: groups, unassignedInitiatives: [], orderedGroupKeys: orderedKeys };
    } else if (groupBy === TableGroupBy.Source) {
      // Group by source
      const sourceMap = new Map<string, InitiativeRecord[]>();
      const unmappedInitiatives: InitiativeRecord[] = [];

      for (const initiative of initiatives) {
        const source = initiative.source;
        if (!source) {
          unmappedInitiatives.push(initiative);
          continue;
        }
        if (!sourceMap.has(source)) {
          sourceMap.set(source, []);
        }
        sourceMap.get(source)!.push(initiative);
      }

      for (const source of Array.from(sourceMap.keys()).sort()) {
        groups[source] = sourceMap.get(source)!;
      }

      if (unmappedInitiatives.length > 0) {
        groups['unmapped'] = unmappedInitiatives;
      }

      return {
        groupedInitiatives: groups,
        unassignedInitiatives: [],
        orderedGroupKeys: Object.keys(groups),
      };
    }

    return { groupedInitiatives: {}, unassignedInitiatives: [], orderedGroupKeys: [] };
  }, [initiatives, outcomes, groupBy]);

  const tableColumns = useMemo(() => getTableColumns(i18n.t, outcomes, false), [outcomes, i18n.t]);

  if (!filtersApplied) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  if (initiatives.length === 0) {
    return <ContentPlaceholder />;
  }

  const handleGroupToggle = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const getGroupLabel = (groupId: string): string => {
    if (groupBy === TableGroupBy.Outcome) {
      const outcome = outcomes.find((o) => o.id === groupId);
      if (outcome) {
        return `${outcome.key} - ${outcome.name}`;
      }
      return groupId;
    }
    if (groupBy === TableGroupBy.Team) {
      if (groupId === 'no-team') {
        return `No ${i18n.t('common.team')}`;
      }
      // Team names are already stored as keys
      return groupId;
    }
    if (groupBy === TableGroupBy.Status) {
      // statusLabels covers every Status enum value (including 'unknown') and
      // resolves through the language-subscribed t(), so a single lookup
      // handles overrides for every status; fall back to the raw groupId
      // only for unrecognized values that aren't in the enum.
      return statusLabels[groupId as keyof typeof statusLabels] || groupId;
    }
    if (groupBy === TableGroupBy.Source) {
      if (groupId === 'bloomfilter') {
        return 'Bloomfilter';
      } else if (groupId === 'unmapped') {
        return 'Unmapped';
      }

      return SOURCE_LABELS[groupId as keyof typeof SOURCE_LABELS] || groupId;
    }
    return groupId;
  };

  return (
    <HiddenScrollArea h="100%" type="auto">
      <Stack gap="md" p="md">
        {orderedGroupKeys.map((groupId) => {
          const initiativesInGroup = groupedInitiatives[groupId];
          if (!initiativesInGroup || initiativesInGroup.length === 0) {
            return null;
          }

          const isExpanded = expandedGroups[groupId] ?? false;
          const groupLabel = getGroupLabel(groupId);

          return (
            <Stack key={groupId} gap="xs">
              <GroupHeaderContainer onClick={() => handleGroupToggle(groupId)}>
                <Icon
                  name={isExpanded ? 'expand_more' : 'chevron_right'}
                  size={20}
                  color={inkBase}
                  style={{ cursor: 'pointer' }}
                />
                <GroupHeaderTitle>
                  <Text size="small" color={inkBase} style={{ fontWeight: 600 }}>
                    {groupLabel}
                  </Text>
                  <GroupCount>{initiativesInGroup.length}</GroupCount>
                </GroupHeaderTitle>
              </GroupHeaderContainer>

              {isExpanded && (
                <TableWrapper>
                  <Table<InitiativeRecord>
                    data={initiativesInGroup}
                    columns={tableColumns}
                    getRowId={(initiative) => initiative.id}
                    onRowClick={setSelectedInitiative}
                    selectedRowId={selectedInitiative?.id}
                    customSort={sortTableWithOutcomes}
                    stickyHeader={false}
                  />
                </TableWrapper>
              )}
            </Stack>
          );
        })}

        {/* Render unassigned group if there are any */}
        {unassignedInitiatives.length > 0 && (
          <Stack gap="xs">
            <GroupHeaderContainer onClick={() => handleGroupToggle('unassigned')}>
              <Icon
                name={expandedGroups['unassigned'] ? 'expand_more' : 'chevron_right'}
                size={20}
                color={inkBase}
                style={{ cursor: 'pointer' }}
              />
              <GroupHeaderTitle>
                <Text size="small" color={inkBase} style={{ fontWeight: 600 }}>
                  {`No ${i18n.t('common.outcome')}`}
                </Text>
                <GroupCount>{unassignedInitiatives.length}</GroupCount>
              </GroupHeaderTitle>
            </GroupHeaderContainer>

            {expandedGroups['unassigned'] && (
              <TableWrapper>
                <Table<InitiativeRecord>
                  data={unassignedInitiatives}
                  columns={tableColumns}
                  getRowId={(initiative) => initiative.id}
                  onRowClick={setSelectedInitiative}
                  selectedRowId={selectedInitiative?.id}
                  customSort={sortTableWithOutcomes}
                  stickyHeader={false}
                />
              </TableWrapper>
            )}
          </Stack>
        )}
      </Stack>
    </HiddenScrollArea>
  );
};

const HiddenScrollArea = styled(ScrollArea)`
  .mantine-ScrollArea-scrollbar {
    display: none;
  }
`;

const GroupHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: ${skyLight};
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${skyLighter};
  }
`;

const GroupHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const GroupCount = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: ${secondaryBase};
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
`;

const TableWrapper = styled.div`
  margin-top: 8px;
  border-left: 2px solid ${secondaryBase};
  padding-left: 8px;
`;
